import cn from '@integrations/classnames'
import { extractRecurringIbanFees, useCurrencies } from 'mmfintech-backend-api'
import type { BankAccountFeeResponse } from 'mmfintech-backend-api/types'
import { formatMoney, isValidArray, tr } from 'mmfintech-commons'

import styles from './IbanRequest.module.scss'

function recurringFeeLabel(feeType?: string) {
  return feeType?.includes('MONTHLY')
    ? tr('FRONTEND.IBAN_REQUEST.MAINTENANCE_FEE', 'Monthly maintenance fee')
    : tr('FRONTEND.IBAN_REQUEST.OTHER_FEE', 'Recurring fee')
}

interface Props {
  fees?: BankAccountFeeResponse[]
  /** On the Confirm step we're consenting to the recurring charge, so the copy is explicit. */
  consent?: boolean
}

/**
 * Renders a block for each recurring (non one-time) fee returned alongside the issuing fee —
 * e.g. a monthly maintenance fee. Charged later on a recurring basis, not at issuance time.
 */
function RecurringFees({ fees, consent = false }: Props) {
  const { getCurrencyPrecision } = useCurrencies()

  const recurring = extractRecurringIbanFees(fees)
  if (!isValidArray(recurring)) return null

  return (
    <>
      {recurring.map((fee) => (
        <div className={cn(styles['iban-recurring-fee'])} key={fee.feeType} data-test='iban-recurring-fee'>
          <div className={cn(styles['iban-recurring-label'])}>{recurringFeeLabel(fee.feeType)}</div>
          <div className={cn(styles['iban-recurring-amount'])}>
            {formatMoney(fee.value, fee.feeCurrency, getCurrencyPrecision(fee.feeCurrency))}
            <span className={cn(styles['iban-recurring-cadence'])}>
              {' '}
              {tr('FRONTEND.IBAN_REQUEST.PER_MONTH', '/ month')}
            </span>
          </div>
          <div className={cn(styles['iban-recurring-sub'])}>
            {consent
              ? tr(
                  'FRONTEND.IBAN_REQUEST.MAINTENANCE_CONSENT',
                  'Charged monthly, starting after your IBAN is issued — not today. By continuing you agree to this recurring fee.'
                )
              : tr(
                  'FRONTEND.IBAN_REQUEST.MAINTENANCE_SUB',
                  'Charged every month, starting after your IBAN is issued — not today.'
                )}
          </div>
        </div>
      ))}
    </>
  )
}

export default RecurringFees
